import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, FileText, Building2, Package, Calendar, Clock, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatDate, formatDateTime } from '@/lib/format';
import { toast } from 'sonner';
import axios from 'axios';

const STATUS_LABELS = {
  OPEN: 'Aberto',
  IN_PROGRESS: 'Em Andamento',
  WAITING_CUSTOMER: 'Aguardando Cliente',
  WAITING_PARTS: 'Aguardando Peças',
  IN_MAINTENANCE: 'Em Manutenção',
  SENT_TO_CLIENT: 'Enviado ao Cliente',
  FINALIZED: 'Finalizado',
  CANCELLED: 'Cancelado',
};

const STATUS_COLORS = {
  OPEN: 'bg-blue-100 text-blue-700',
  IN_PROGRESS: 'bg-yellow-100 text-yellow-700',
  WAITING_CUSTOMER: 'bg-orange-100 text-orange-700',
  WAITING_PARTS: 'bg-purple-100 text-purple-700',
  IN_MAINTENANCE: 'bg-indigo-100 text-indigo-700',
  SENT_TO_CLIENT: 'bg-cyan-100 text-cyan-700',
  FINALIZED: 'bg-green-100 text-green-700',
  CANCELLED: 'bg-slate-100 text-slate-700',
};

const PRIORITY_LABELS = {
  LOW: 'Baixa',
  MEDIUM: 'Média',
  HIGH: 'Alta',
  URGENT: 'Urgente',
};

export default function PublicTicketDetailPage() {
  const { protocol } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTicket();
  }, [protocol]);

  const fetchTicket = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const headers = {};
      if (location.state?.recaptchaToken) {
        headers['x-recaptcha-token'] = location.state.recaptchaToken;
      }
      
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL || 'http://localhost:3001/api'}/public/tickets/by-protocol/${protocol}`,
        { headers }
      );
      
      setTicket(response.data);
    } catch (err) {
      console.error('Error fetching ticket:', err);
      setError(err.response?.data?.message || 'Erro ao buscar ticket');
      toast.error(err.response?.data?.message || 'Erro ao buscar ticket');
    } finally {
      setLoading(false);
    }
  };

  const calculateSLAStatus = () => {
    if (!ticket?.slaDueAt) return null;
    
    const now = new Date();
    const dueDate = new Date(ticket.slaDueAt);
    const diffMs = dueDate - now;
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (ticket.isSlaExceeded) {
      return {
        label: `Vencido há ${Math.abs(diffDays)} ${Math.abs(diffDays) === 1 ? 'dia' : 'dias'}`,
        color: 'text-red-600',
        icon: AlertCircle,
      };
    }

    if (diffDays === 0) {
      return {
        label: 'Vence hoje',
        color: 'text-orange-600',
        icon: Clock,
      };
    }

    if (diffDays > 0) {
      return {
        label: `${diffDays} ${diffDays === 1 ? 'dia' : 'dias'} restantes`,
        color: 'text-green-600',
        icon: CheckCircle2,
      };
    }

    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Carregando ticket...</p>
        </div>
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-2xl mx-auto">
            <Card className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Ticket não encontrado</h2>
              <p className="text-slate-600 mb-6">{error}</p>
              <Button onClick={() => navigate('/consulta')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar para consulta
              </Button>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  const slaStatus = calculateSLAStatus();
  const SLAIcon = slaStatus?.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center">
                <FileText className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">{ticket.protocol}</h1>
                <p className="text-sm text-slate-600">{ticket.companyName}</p>
              </div>
            </div>
            <Button variant="outline" onClick={() => navigate('/consulta')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Nova Consulta
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Status Card */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-900">Status do Ticket</h2>
              <Badge className={STATUS_COLORS[ticket.status]}>
                {STATUS_LABELS[ticket.status]}
              </Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-slate-500 mb-1">Prioridade</p>
                <p className="font-medium text-slate-900">{PRIORITY_LABELS[ticket.priority]}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Data de Abertura</p>
                <p className="font-medium text-slate-900">{formatDate(ticket.openedAt)}</p>
              </div>
              {slaStatus && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">Prazo SLA</p>
                  <div className={`flex items-center gap-2 font-medium ${slaStatus.color}`}>
                    {SLAIcon && <SLAIcon className="w-4 h-4" />}
                    <span>{slaStatus.label}</span>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Details Card */}
          <Card className="p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-4">Detalhes</h2>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-slate-500 mb-1">Produto</p>
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-slate-400" />
                  <p className="font-medium text-slate-900">{ticket.productName}</p>
                </div>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Categoria</p>
                <p className="font-medium text-slate-900">{ticket.categoryName}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Localização</p>
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-slate-400" />
                  <p className="font-medium text-slate-900">{ticket.city}/{ticket.state}</p>
                </div>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Motivo</p>
                <p className="font-medium text-slate-900">{ticket.reason}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Descrição do Problema</p>
                <p className="text-slate-700 whitespace-pre-wrap">{ticket.problemDescription}</p>
              </div>
            </div>
          </Card>

          {/* History Card */}
          {ticket.statusHistory && ticket.statusHistory.length > 0 && (
            <Card className="p-6">
              <h2 className="text-lg font-bold text-slate-900 mb-4">Histórico de Status</h2>
              <div className="space-y-4">
                {ticket.statusHistory.map((history, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <Clock className="w-4 h-4 text-blue-600" />
                      </div>
                      {index < ticket.statusHistory.length - 1 && (
                        <div className="w-0.5 h-full bg-slate-200 my-1"></div>
                      )}
                    </div>
                    <div className="flex-1 pb-4">
                      <div className="flex items-center gap-2 mb-1">
                        {history.previousStatus && (
                          <>
                            <Badge variant="outline" className="text-xs">
                              {STATUS_LABELS[history.previousStatus]}
                            </Badge>
                            <span className="text-slate-400">→</span>
                          </>
                        )}
                        <Badge className={`text-xs ${STATUS_COLORS[history.newStatus]}`}>
                          {STATUS_LABELS[history.newStatus]}
                        </Badge>
                      </div>
                      {history.note && (
                        <p className="text-sm text-slate-600 mt-1">{history.note}</p>
                      )}
                      <p className="text-xs text-slate-400 mt-1">{formatDateTime(history.createdAt)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
